import Immutable from 'immutable';
import type Luminary from '@lumen5/luminary';
import type { AspectRatio, OpaqueVideoData, TemplateBundle } from '@lumen5/luminary';
import { TemplateColorProperties, VideoData } from '@lumen5/luminary';
import { createSelector } from '@reduxjs/toolkit';

import type {
    DisplayGroup,
    SceneDesign,
    SceneDesignIdsPerTemplate,
    Template,
    TemplateCategory,
} from 'js/src/client';
import { GlobalTemplate, TemplateStatusEnum } from 'js/src/client';
import { CLIP_CREATOR_PREVIEW_TEMPLATE } from 'js/src/data/clip-creator/clip-creator-consts';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import StoreORM, { BY_ID } from 'js/src/data/store-orm';
import {
    isTemplatesListRequestFetched,
    isTemplatesListRequestFetching,
} from 'js/src/data/templates/slices/loadingInfoSlice';
import type {
    ChartColorOverrides,
    PaletteData,
    QATemplateBundle,
} from 'js/src/data/templates/templates-types';
import type { Template as ValidationTemplate } from 'js/src/editor/validation/validationTypes';
import type { ImmutableMap, TemplateCategoryId } from 'js/src/libs/consts';
import {
    DYNAMIC_DISPLAY_GROUPS,
    ImmutableFromJS,
    SceneDesignSpecialCase,
} from 'js/src/libs/consts';
import Flags from 'js/src/libs/flags';
import type { RootState } from 'js/src/libs/RootState';
// Categories
export function getCategoryIds(state: RootState): Immutable.List<number> {
    const orm = new StoreORM<TemplateCategory>(state.templates.templateCategories);
    return orm.getAllIds();
}

/**
 * Category IDs shown to users when selecting a template, excluding any categories
 * marked as hidden. Hidden categories remain available to internal template editing
 * tools (see getCategoryIds).
 */
export const getTemplateCategoryIds = createSelector(
    [(state: RootState) => state.templates.templateCategories],
    (templateCategories): Immutable.List<TemplateCategoryId> => {
        const templateCategoryOrm = new StoreORM<TemplateCategory>(templateCategories);
        return templateCategoryOrm
            .getAllIds()
            .filter((categoryId) => !templateCategoryOrm.getById(categoryId).get('hidden'));
    },
);

export function getTemplateIdsForACategory(
    state: RootState,
    categoryId: TemplateCategoryId,
): Immutable.List<string> {
    const orm = new StoreORM<TemplateCategory>(state.templates.templateCategories);
    const category = orm.getById(categoryId);
    return category ? category.get('templates') : Immutable.List();
}

export function getCategoryById(
    state: RootState,
    categoryId: TemplateCategoryId,
): ImmutableMap<TemplateCategory> {
    const orm = new StoreORM<TemplateCategory>(state.templates.templateCategories);
    return orm.getById(categoryId);
}

export function getDraftCategoryId(state: RootState): number {
    // Returns the [DRAFT] category if it can be found, otherwise falls back to the first category
    const orm = new StoreORM<TemplateCategory>(state.templates.templateCategories);
    const matches = orm.query({
        key: ['display_name'],
        value: '[DRAFT]',
    });
    if (matches.size > 0) {
        return matches.get(0)!.get('id');
    }
    const firstCategoryId = orm.getAllIds().get(0);
    if (firstCategoryId === undefined) {
        throw new Error('No categories found');
    }
    return firstCategoryId;
}

// Templates
export function hasTemplate(state: RootState, templateId: string): boolean {
    const orm = new StoreORM<Template, string>(state.templates.templatesList);
    return !!orm.getByIdOrUndefined(templateId);
}

export function hasSceneDesignsForTemplate(state: RootState, templateId: string): boolean {
    const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(
        state.templates.sceneDesignIdsByTemplate,
    );
    const sceneDesignIds = orm.getByIdOrUndefined(templateId);
    return !!sceneDesignIds && sceneDesignIds.get('scene_design_ids').size > 0;
}

export function areTemplatesLoading(state: RootState): boolean {
    return isTemplatesListRequestFetching(state);
}

export const areTemplatesLoaded = (state: RootState): boolean => {
    const templatesList = state.templates.templatesList;
    const templatesListOrm = new StoreORM<Template, string>(templatesList);
    const templatesListSize = templatesListOrm.length;
    return isTemplatesListRequestFetched(state) && templatesListSize > 0;
};

export const areAllSceneDesignsLoadedForTemplate = (
    state: RootState,
    templateId: string,
): boolean => {
    const templateSceneIds = getTemplateSceneIds(state, templateId);
    const allScenesFetched = templateSceneIds.every((sceneId) => !!getSceneDesign(state, sceneId));
    return templateSceneIds.size > 0 && allScenesFetched;
};

export function isTemplateBundleLoading(state: RootState, templateId: string): boolean {
    const templateBundle = getTemplateBundle(state, templateId);
    return templateBundle === null || Object.keys(templateBundle.sceneDesigns).length === 0;
}

export const templatesLoadedStatuses = createSelector(
    (state: RootState) => state.templates.templatesList,
    (state: RootState) => state.templates.sceneDesignIdsByTemplate,
    (state: RootState) => state.templates.sceneDesigns,
    (templatesList, sceneDesignIdsByTemplate, sceneDesigns) => {
        const templatesOrm = new StoreORM<Template, string>(templatesList);
        const sceneDesignsIdsOrm = new StoreORM<SceneDesignIdsPerTemplate, string>(
            sceneDesignIdsByTemplate,
        );
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesigns);
        const templateIds = templatesOrm.getAllIds();
        let result = Immutable.Map<string, boolean>();
        templateIds.forEach((templateId: string) => {
            const template = templatesOrm.getById(templateId);
            if (!template || !template.get('template_data')) {
                result.set(templateId, false);
                return;
            }
            if (!sceneDesignsIdsOrm.getById(templateId)) {
                result.set(templateId, false);
                return;
            }
            const sceneDesignIds = sceneDesignsIdsOrm.getById(templateId).get('scene_design_ids');
            if (sceneDesignIds.size === 0) {
                result.set(templateId, false);
                return;
            }
            const sceneDesignsList = sceneDesignIds.map((id) => sceneDesignsOrm.getById(id));
            if (sceneDesignsList.some((sceneDesign) => !sceneDesign)) {
                result.set(templateId, false);
                return;
            }
            result = result.set(templateId, true);
        });
        return result;
    },
);

export function getTemplateIds(state: RootState): Immutable.List<string> {
    const orm = new StoreORM<Template, string>(state.templates.templatesList);
    return orm.getAllIds();
}

export function getTemplateById(state: RootState, templateId: string) {
    const orm = new StoreORM<Template, string>(state.templates.templatesList);
    return orm.getByIdOrUndefined(templateId);
}

/**
 * Before call operations on luminary
 * which require scene designs or template data (addSlide, set aspectRatio)
 * we should pass all information about sceneDesigns and template.
 * Object which contains this information called templateBundle
 * In redux store we keep templates and sceneDesigns separately and we use redux selectors
 * in order to create templateBundle object.
 * This cache contains templateBundles
 */
type TemplateBundleCacheEntry = {
    bundle: TemplateBundle;
    templateDataRef: unknown; // Immutable reference for change detection
    sceneDesignRefs: Record<string, unknown>; // PlainJsStoreORM references per scene design
};

const templateBundlesCache = new Map<string, TemplateBundleCacheEntry | null>();

export function getTemplateBundle(state: RootState, templateId: string): TemplateBundle | null {
    const templatesListOrm = new StoreORM<Template, string>(state.templates.templatesList);
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);

    const template = templatesListOrm.getByIdOrUndefined(templateId);
    const sceneDesignIds = getTemplateSceneIds(state, templateId);

    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);

    if (!template || sceneDesignIds.size === 0) {
        templateBundlesCache.set(templateId, null);
        return null;
    }

    const templateDataImmutable = template.get('template_data') as
        | ImmutableMap<OpaqueVideoData>
        | undefined;
    if (!templateDataImmutable) {
        templateBundlesCache.set(templateId, null);
        return null;
    }

    // Check if cached entry is still valid by comparing source references
    const cached = templateBundlesCache.get(templateId);
    if (cached) {
        let cacheValid = cached.templateDataRef === templateDataImmutable;
        if (cacheValid && Object.keys(cached.sceneDesignRefs).length !== sceneDesignIds.size) {
            cacheValid = false;
        }
        if (cacheValid) {
            sceneDesignIds.forEach((sceneDesignId: string) => {
                const sceneDesignData = sceneDesignsOrm.getById(sceneDesignId);
                if (cached.sceneDesignRefs[sceneDesignId] !== sceneDesignData) {
                    cacheValid = false;
                }
            });
        }
        if (cacheValid) {
            return cached.bundle;
        }
    }

    const sceneDesigns: TemplateBundle['sceneDesigns'] = {};
    const sceneDesignRefs: Record<string, unknown> = {};

    sceneDesignIds.forEach((sceneDesignId: string) => {
        const sceneDesignData = sceneDesignsOrm.getById(sceneDesignId);
        if (!sceneDesignData) {
            return;
        }

        sceneDesignRefs[sceneDesignId] = sceneDesignData;
        const aspectRatioMap = {} as { [K in AspectRatio]: OpaqueVideoData | null };
        VideoData.ALL_ASPECT_RATIOS.forEach((aspectRatio) => {
            if (!supportedAspectRatios.includes(aspectRatio)) {
                return;
            }
            const aspectRatioDataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const aspectRatioData = sceneDesignData[aspectRatioDataKey] as OpaqueVideoData;
            if (!aspectRatioData) {
                return;
            }

            aspectRatioMap[aspectRatio] = aspectRatioData;
        });

        if (Object.keys(aspectRatioMap).length > 0) {
            sceneDesigns[sceneDesignId] = aspectRatioMap;
        }
    });

    const bundle: TemplateBundle = {
        templateData: templateDataImmutable.toJS(),
        sceneDesigns,
    };

    templateBundlesCache.set(templateId, {
        bundle,
        templateDataRef: templateDataImmutable,
        sceneDesignRefs,
    });
    return bundle;
}

export const getSupportedAspectRatios = createSelector(
    [
        (state: RootState, templateId: string | null) => templateId,
        (state: RootState, templateId: string | null) => {
            if (!templateId) {
                return null;
            }
            return getTemplateById(state, templateId) || null;
        },
    ],
    (templateId, template) => {
        if (!templateId || !template) {
            return [] as AspectRatio[];
        }

        const supportedAspectRatios = template.get(
            'supported_aspect_ratios',
            Immutable.List(),
        ) as Immutable.List<AspectRatio>;

        return supportedAspectRatios.toArray();
    },
);

export function getTemplateTeams(
    state: RootState,
    templateId: string | null,
): Immutable.List<number> {
    if (!templateId) {
        return Immutable.List();
    }
    const template = getTemplateById(state, templateId);

    if (!template) {
        return Immutable.List();
    }

    return template.get('teams');
}

type QATemplateBundleCacheEntry = {
    bundle: QATemplateBundle;
    baseBundleRef: TemplateBundle;
};

const qaTemplateBundleCache = new Map<string, QATemplateBundleCacheEntry | null>();

export function getQATemplateBundle(state: RootState, templateId: string): QATemplateBundle | null {
    const baseBundle = getTemplateBundle(state, templateId);
    if (!baseBundle) {
        qaTemplateBundleCache.set(templateId, null);
        return null;
    }

    const cached = qaTemplateBundleCache.get(templateId);
    if (cached && cached.baseBundleRef === baseBundle) {
        return cached.bundle;
    }

    const sceneDesignIds = getTemplateSceneIds(state, templateId);

    const templatesListOrm = new StoreORM<Template, string>(state.templates.templatesList);
    const template = templatesListOrm.getById(templateId);

    if (!template) {
        throw new Error(`Template with id ${templateId} not found`);
    }

    const sceneDesigns: QATemplateBundle['sceneDesigns'] = {
        ...baseBundle.sceneDesigns,
    } as QATemplateBundle['sceneDesigns'];
    sceneDesignIds.forEach((sceneDesignId: string) => {
        const sceneAttributesRecommendation = getSceneAttributesRecommendation(
            state,
            sceneDesignId,
        );
        if (sceneAttributesRecommendation && sceneDesigns[sceneDesignId]) {
            sceneDesigns[sceneDesignId] = {
                ...sceneDesigns[sceneDesignId],
                scene_attributes_recommendation: sceneAttributesRecommendation,
            };
        }
    });

    const paletteDataImmutable = template.get('palette_data') as
        | ImmutableMap<PaletteData[]>
        | undefined;
    const paletteDatas = paletteDataImmutable ? paletteDataImmutable.toJS() : [];

    const bundle: QATemplateBundle = {
        ...baseBundle,
        sceneDesigns,
        paletteDatas,
        textMinSize: template.get('text_min_size'),
        textMaxSize: template.get('text_max_size'),
    };

    qaTemplateBundleCache.set(templateId, { bundle, baseBundleRef: baseBundle });
    return bundle;
}

/**
 * Creates a special bundle of data that is passed to the validator.
 */
export function getTemplateValidationData(
    state: RootState,
    templateId: string,
): ImmutableMap<ValidationTemplate> {
    const templatesListOrm = new StoreORM<Template, string>(state.templates.templatesList);
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);
    const displayGroupsOrm = new StoreORM<DisplayGroup>(state.templates.displayGroups);
    const template = templatesListOrm.getById(templateId);
    const sceneDesignIds = getTemplateSceneIds(state, templateId);
    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);
    let validationData = template as ImmutableMap<ValidationTemplate>;

    if (!(template && sceneDesignsOrm.length > 0)) {
        throw new Error(`Template with id ${templateId} not found`);
    }

    validationData = validationData.set('scene_designs', Immutable.List());
    sceneDesignIds.forEach((sceneDesignId, index) => {
        const sceneDesignData = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
        validationData = validationData.setIn(['scene_designs', index, 'id'], sceneDesignData.id);
        validationData = validationData.setIn(
            ['scene_designs', index, 'status'],
            sceneDesignData.status,
        );
        supportedAspectRatios.forEach((aspectRatio) => {
            const aspectRatioDataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const aspectRatioData = sceneDesignData[aspectRatioDataKey];
            if (!aspectRatioData) {
                return;
            }
            validationData = validationData.setIn(
                ['scene_designs', index, aspectRatioDataKey],
                aspectRatioData,
            );
        });
        validationData = validationData.setIn(
            ['scene_designs', index, 'scene_attributes_recommendation'],
            getSceneAttributesRecommendation(state, sceneDesignId),
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'display_group'],
            sceneDesignData.display_group,
        );
        validationData = validationData.setIn(
            ['scene_designs', index, 'non_recommendable_assets'],
            sceneDesignData.non_recommendable_assets,
        );
        const displayGroup = displayGroupsOrm.getById(sceneDesignData.display_group).toJS();
        validationData = validationData.setIn(
            ['scene_designs', index, 'objects', 'display_group'],
            displayGroup,
        );
        const displayGroups = getDisplayGroupIds(state)
            .map((id) => displayGroupsOrm.getById(id))
            .toJS();
        validationData = validationData.setIn(
            ['scene_designs', index, 'objects', 'display_groups'],
            displayGroups,
        );
    });

    return validationData;
}

export function getSceneDesign(
    state: RootState,
    sceneDesignId: string | null | undefined,
): SceneDesign | undefined | null {
    if (!sceneDesignId) return null;
    const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(state.templates.sceneDesigns);
    return sceneDesignsOrm.getById(sceneDesignId);
}

export function getSceneAttributesRecommendation(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign && sceneDesign.scene_attributes_recommendation;
}

export function getSceneDesignStatus(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign && sceneDesign.status;
}

export function getSceneDesignDisplayName(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    const displayName = sceneDesign && sceneDesign.display_name;
    return displayName !== null ? displayName : '';
}

export function getTemplateData(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && (template.get('template_data') as ImmutableMap<OpaqueVideoData>);
}

export function getTemplateStatus(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('status');
}

export function getTemplateDefaultTextSizeOptions(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('default_text_size_options');
}

export function getTemplateDisplayName(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('display_name');
}

export function getTemplateImageUrl(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('image_url');
}

export function getTemplateMp4Url(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('mp4_url');
}

export function getTemplateAspectRatioMp4Urls(
    state: RootState,
    templateId: string,
): Record<AspectRatio, string> | null {
    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);

    if (supportedAspectRatios.length === 0) {
        return null;
    }

    const template = getTemplateById(state, templateId);
    if (!template) {
        return null;
    }
    const templateMp4Urls: Record<AspectRatio, string> = Object.fromEntries(
        supportedAspectRatios
            .map((aspectRatio: AspectRatio) => {
                const url = template.get(`${aspectRatio}_mp4_url`) as string;
                return url ? [aspectRatio, url] : null;
            })
            .filter((entry): entry is [AspectRatio, string] => entry !== null),
    ) as Record<AspectRatio, string>;

    return templateMp4Urls;
}

export function getTemplateTextMinSize(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('text_min_size');
}

export function getTemplateTextMaxSize(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return template && template.get('text_max_size');
}

export function getTemplateTextDefaultSizes(
    state: RootState,
    templateId: string,
): Immutable.List<number> {
    const template = getTemplateById(state, templateId);
    if (!template) {
        return Immutable.List();
    }
    return template.get('default_text_size_options');
}

export function getTemplateColorPalettes(
    state: RootState,
    templateId: string,
): Immutable.List<ImmutableMap<PaletteData>> {
    const template = getTemplateById(state, templateId);
    if (!template) {
        return Immutable.List();
    }
    return template.get('palette_data', Immutable.List()) as Immutable.List<
        ImmutableMap<PaletteData>
    >;
}

export function getChartColorOverrides(state: RootState, templateId: string): ChartColorOverrides {
    const template = getTemplateById(state, templateId);
    const overrides = template?.get('chart_color_overrides') as ChartColorOverrides | undefined;
    return overrides ?? Immutable.Map<string, string>();
}

// returns color codes for the template's default palettes, where each palette is represented by an array
export const getTemplatePaletteColorCodes = createSelector(
    (state: RootState, templateId: string) => getTemplateColorPalettes(state, templateId),
    (palettes) => {
        return palettes.toArray().map((palette) => {
            const paletteTemplateColors = palette.get('colors').toArray();
            return paletteTemplateColors.map((color) => color.get(TemplateColorProperties.COLOR));
        });
    },
);

export function getTemplateDefaultSceneDesign(state: RootState, templateId: string): string | null {
    // default_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.get('default_scene_design') ?? null;
}

export function getTemplateDefaultTalkingHeadSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_talking_head_scene_design is required for any templates that support talking head scenes
    const template = getTemplateById(state, templateId);
    return template?.get('default_talking_head_scene_design') ?? null;
}

export function getTemplateDefaultCutawaySceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_cutaway_scene_design is required for any templates that support voiceover scenes
    const template = getTemplateById(state, templateId);
    return template?.get('default_cutaway_scene_design') ?? null;
}

export function getTemplateDefaultStoryboardSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_storyboard_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.get('default_storyboard_scene_design') ?? null;
}

export function getTemplateDefaultOutroSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_outro_scene_design should be set for all templates except super old templates
    const template = getTemplateById(state, templateId);
    return template?.get('default_outro_scene_design') ?? null;
}

export function getTemplateDefaultIconSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_icon_scene_design should be set for all templates that support icons (pretty much all of them!)
    const template = getTemplateById(state, templateId);
    return template?.get('default_icon_scene_design') ?? null;
}

export function getTemplateDefaultCaptionSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_caption_scene_design should be set for all templates that support voiceover or talking head
    const template = getTemplateById(state, templateId);
    return template?.get('default_caption_scene_design') ?? null;
}

export function getTemplateDefaultVoiceoverSceneDesign(
    state: RootState,
    templateId: string,
): string | null {
    // default_voiceover_scene_design is required for any templates that support voiceover scenes
    const template = getTemplateById(state, templateId);
    return template?.get('default_voiceover_scene_design') ?? null;
}

export function getSceneCategoryFromSceneDesign(
    sceneDesign: SceneDesign,
    supportedAspectRatios: AspectRatio[],
): string {
    const aspectRatio = supportedAspectRatios[0];
    const firstSupportedAspectRatioKey = `${aspectRatio}_data` as keyof SceneDesign;

    return VideoData.getSceneCategoryFromSceneData(
        sceneDesign[firstSupportedAspectRatioKey] as OpaqueVideoData,
    );
}

export function getTemplateSceneIds(
    state: RootState,
    templateId: string | null,
    opts?: { excludeGlobalTemplate?: boolean },
): Immutable.List<string> {
    const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(
        state.templates.sceneDesignIdsByTemplate,
    );
    const sceneDesignIds = templateId ? orm.getByIdOrUndefined(templateId) : null;
    if (!sceneDesignIds) {
        return Immutable.List<string>();
    }
    const ids = sceneDesignIds.get('scene_design_ids');
    if (opts?.excludeGlobalTemplate) {
        return ids.filter((id) => {
            const sceneDesignTemplateId = VideoData.getTemplateIdFromSceneDesignId(id);
            return !isGlobalTemplate(sceneDesignTemplateId);
        });
    }
    return ids;
}

export function getAllSceneDesignIdsPerTemplate(
    state: RootState,
): Immutable.Map<string, ImmutableMap<SceneDesignIdsPerTemplate>> {
    // NOTE: the .get(BY_ID) typing is wrong
    return state.templates.sceneDesignIdsByTemplate.get(BY_ID) as unknown as Immutable.Map<
        string,
        ImmutableMap<SceneDesignIdsPerTemplate>
    >;
}

export const getTemplateSceneDesigns = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) =>
        getTemplateDefaultTalkingHeadSceneDesign(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (
        sceneDesignsInState,
        sceneDesignIds,
        defaultTalkingHeadSceneDesign,
        supportedAspectRatios,
    ): SceneDesign[] => {
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        sceneDesignIds.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (canUserSee(sceneDesign, sceneCategory, defaultTalkingHeadSceneDesign)) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

export const getCaptionsSceneDesignsForTemplate = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesignsInState, templateSceneDesigns, supportedAspectRatios): SceneDesign[] => {
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        templateSceneDesigns.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (
                sceneCategory === VideoData.CATEGORY_CAPTION &&
                sceneDesign.status === TemplateStatusEnum.ACTIVE
            ) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

export const getVoiceoverSceneDesignsForTemplate = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesignsInState, templateSceneDesigns, supportedAspectRatios): SceneDesign[] => {
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        templateSceneDesigns.forEach((sceneDesignId) => {
            const sceneDesign = sceneDesignsOrm.getByIdOrThrow(sceneDesignId);
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (
                sceneCategory === VideoData.CATEGORY_VOICEOVER &&
                sceneDesign.status === TemplateStatusEnum.ACTIVE
            ) {
                result.push(sceneDesign);
            }
        });
        return result;
    },
);

/*
 * getTemplateSceneDesigns is used in the app as well as in the editor.
 * Unfortunately that means that some scene designs are filtered out
 * of the editor depending on the feature flags of the internal users
 * which is not what we want. This method is only to be used for
 * internal apps, not for customers.
 */
export const INTERNAL_getTemplateSceneDesigns = createSelector(
    (state: RootState) => state.templates.sceneDesigns,
    (state: RootState, templateId: string | null) => getTemplateSceneIds(state, templateId),
    (sceneDesignsInState, sceneDesignIds): SceneDesign[] => {
        const sceneDesignsOrm = new PlainJsStoreORM<SceneDesign, string>(sceneDesignsInState);
        const result: SceneDesign[] = [];
        sceneDesignIds.forEach((sceneDesignId) => {
            result.push(sceneDesignsOrm.getByIdOrThrow(sceneDesignId));
        });
        return result;
    },
);

export const getFullscreenSelectorSceneDesigns = createSelector(
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneDesigns, supportedAspectRatios): SceneDesign[] =>
        sceneDesigns.filter((sceneDesign) => {
            const sceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            if (sceneCategory === VideoData.CATEGORY_CAPTION) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
                return false;
            }
            if (sceneCategory === VideoData.CATEGORY_VOICEOVER) {
                return false;
            }
            return true;
        }),
);

export const getTemplateSceneDesignsByCategory = createSelector(
    (state: RootState, templateId: string, sceneCategory: string) => sceneCategory,
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneCategory, allSceneDesigns, supportedAspectRatios): SceneDesign[] =>
        allSceneDesigns.filter((sceneDesign) => {
            const actualSceneCategory = getSceneCategoryFromSceneDesign(
                sceneDesign,
                supportedAspectRatios,
            );
            return sceneCategory === actualSceneCategory;
        }),
);

export function getLastSceneDesignOrder(state: RootState, templateId: string): number {
    const ids = getTemplateSceneIds(state, templateId);
    if (ids.size === 0) {
        return -1;
    }

    // To be safe, assume the order isn't a sequence, 0, 1, 2, 3 but could be 0, 1, 1, 5.
    const sceneDesign = getSceneDesign(state, ids.get(ids.size - 1));

    if (!sceneDesign) {
        return -1;
    }

    return sceneDesign.order;
}

function canUserSee(
    sceneDesign: SceneDesign,
    sceneCategory: string,
    defaultTalkingHeadSceneDesign: string | null,
): boolean {
    const isTalkingHead = sceneCategory === VideoData.CATEGORY_TALKING_HEAD;
    if (isTalkingHead && sceneDesign.id !== defaultTalkingHeadSceneDesign) {
        return false;
    }
    return Flags.canSeeAllTemplates() || sceneDesign.status === TemplateStatusEnum.ACTIVE;
}

export function canUserSeeSceneDesign(state: RootState, sceneDesignId: string): boolean {
    if (Flags.canSeeAllTemplates()) {
        return true;
    }

    const status = getSceneDesignStatus(state, sceneDesignId);
    return status === TemplateStatusEnum.ACTIVE;
}

export const getTemplateDisplayGroups = createSelector(
    (state: RootState) => state.templates.displayGroups,
    (state: RootState, templateId: string) => templateId,
    (state: RootState, templateId: string, opts?: { excludeGlobalTemplate?: boolean }) =>
        opts?.excludeGlobalTemplate ?? false,
    (displayGroups, templateId, excludeGlobalTemplate) => {
        const orm = new StoreORM<DisplayGroup>(displayGroups);
        const displayGroupList = orm.filter((item) => {
            if (excludeGlobalTemplate) {
                return item.get('template') === templateId;
            }
            return item.get('template') === templateId || isGlobalTemplate(item.get('template'));
        });
        if (!displayGroupList || displayGroupList.isEmpty()) {
            return Immutable.List<ImmutableMap<DisplayGroup>>();
        } else {
            return displayGroupList.sort((dg1, dg2) => dg1.get('order') - dg2.get('order'));
        }
    },
);

// Dynamically create display groups that includes multiple speakers display group.
export const getDynamicTemplateDisplayGroups = createSelector(
    (state: RootState) => state.templates.displayGroups,
    (state: RootState, templateId: string) => templateId,
    (displayGroups, templateId) => {
        const orm = new StoreORM<DisplayGroup>(displayGroups);
        let displayGroupList = orm.filter((item) => item.get('template') === templateId);

        if (!displayGroupList || displayGroupList.isEmpty()) {
            return Immutable.List<ImmutableMap<DisplayGroup>>();
        }

        // Adding the multiple speakers display group to the list, it should always be the second display group
        const uniqueOrders = Array.from(new Set(displayGroupList.map((g) => g.get('order'))));
        uniqueOrders.sort((a, b) => a - b);
        let multipleSpeakersOrder = 1;
        if (uniqueOrders.length === 1) {
            multipleSpeakersOrder = uniqueOrders[0] + 1;
        } else if (uniqueOrders.length > 1) {
            multipleSpeakersOrder = Math.ceil((uniqueOrders[0] + uniqueOrders[1]) / 2);
        }

        const multipleSpeakersDisplayGroup: DisplayGroup = {
            id: DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.MultipleSpeakers],
            display_name: 'Multiple Speakers',
            order: multipleSpeakersOrder,
            template: '',
        };

        displayGroupList = displayGroupList.push(ImmutableFromJS(multipleSpeakersDisplayGroup));

        // Charts appear at the end. They render only when scenesToShow has chart entries
        // for the group (getDisplayGroupsToShow filters out groups with no scenes).
        const chartDisplayGroup: DisplayGroup = {
            id: DYNAMIC_DISPLAY_GROUPS[SceneDesignSpecialCase.Chart],
            display_name: 'Charts',
            order: (uniqueOrders.at(-1) ?? 0) + 1,
            template: '',
        };
        displayGroupList = displayGroupList.push(ImmutableFromJS(chartDisplayGroup));

        return displayGroupList.sort((dg1, dg2) => dg1.get('order') - dg2.get('order'));
    },
);

export function getDisplayGroupById(state: RootState, displayGroupId: number) {
    const orm = new StoreORM<DisplayGroup>(state.templates.displayGroups);
    return orm.getByIdOrUndefined(displayGroupId);
}

export function getTemplateDisplayGroupsAmount(state: RootState, templateId: string): number {
    const orm = new StoreORM<DisplayGroup>(state.templates.displayGroups);
    const templateDisplayGroups = orm.filter(
        (displayGroup) => displayGroup.get('template') === templateId,
    );
    return templateDisplayGroups.size;
}

export function hasTemplateDisplayGroups(state: RootState, templateId: string): boolean {
    const orm = new StoreORM<DisplayGroup>(state.templates.displayGroups);
    const templateDisplayGroups = orm.filter(
        (displayGroup) => displayGroup.get('template') === templateId,
    );
    return templateDisplayGroups.size > 0;
}

export function getSceneDesignDisplayGroup(state: RootState, sceneDesignId: string) {
    const sceneDesign = getSceneDesign(state, sceneDesignId);
    return sceneDesign?.display_group;
}

export function getDisplayGroupIds(state: RootState): Immutable.List<number> {
    const orm = new StoreORM<DisplayGroup>(state.templates.displayGroups);
    return orm.getAllIds();
}

export function getDisplayGroupNameForSceneDesign(state: RootState, sceneDesignId: string): string {
    const displayGroupId = getSceneDesignDisplayGroup(state, sceneDesignId);
    if (!displayGroupId) {
        return '';
    }
    const displayGroup = getDisplayGroupById(state, displayGroupId);
    if (!displayGroup) {
        return '';
    }
    return displayGroup.get('display_name');
}

export const getGroupedSceneDesignIds = createSelector(
    (state: RootState, templateId: string) => getTemplateSceneIds(state, templateId),
    (state: RootState, templateId: string) => INTERNAL_getTemplateSceneDesigns(state, templateId),
    (state: RootState) => state.templates.displayGroups,
    (sceneDesignIds, sceneDesigns, displayGroups) => {
        const globalIds: string[] = [];
        const groupedIds = new Map<number, string[]>();

        const sceneDesignsMap = new Map(sceneDesigns.map((sd) => [sd.id, sd] as const));

        sceneDesignIds.forEach((id) => {
            const sceneDesign = sceneDesignsMap.get(id);
            if (!sceneDesign) return;

            const isGlobal = isGlobalTemplate(sceneDesign.template);
            if (isGlobal) {
                globalIds.push(id);
            } else {
                const groupId = sceneDesign.display_group;
                const ids = groupedIds.get(groupId) ?? [];
                groupedIds.set(groupId, [...ids, id]);
            }
        });

        const groups = [];

        const displayGroupsOrm = new StoreORM(displayGroups);
        Array.from(groupedIds.entries())
            .sort(([a], [b]) => a - b)
            .forEach(([groupId, ids]) => {
                const group = displayGroupsOrm.getByIdOrUndefined(groupId);
                const label = group?.get('display_name') || `Display Group ${groupId}`;
                groups.push({ label, options: ids });
            });

        if (globalIds.length > 0) {
            groups.push({ label: 'Global Designs', options: globalIds });
        }

        return groups;
    },
);

export function getRecentSelectedSceneDesigns(state: RootState): Immutable.List<string> {
    return state.templates.recentSelectedSceneDesigns;
}

export const getCappedRecentSelectedSceneDesigns = createSelector(
    (state: RootState) => getRecentSelectedSceneDesigns(state),
    (recentlySelectedSceneDesigns: ReturnType<typeof getRecentSelectedSceneDesigns>) => {
        const maxRecentDesignsCount = 3;
        return recentlySelectedSceneDesigns.toArray().slice(0, maxRecentDesignsCount);
    },
);

export function getActiveTemplateCategoryId(state: RootState): TemplateCategoryId | null {
    return state.templates.activeTemplateCategoryId;
}

export function isCustomTemplate(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return !!template && template.get('teams').size > 0;
}

export function hasACustomTemplate(state: RootState): boolean {
    const orm = new StoreORM<Template, string>(state.templates.templatesList);
    return orm.find((template) => isCustomTemplate(state, template.get('id'))) !== undefined;
}

export function getTemplateColorShuffleEnabled(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return !!template && template.get('color_shuffle_enabled');
}

export function getTemplateAllowGlobalSceneDesigns(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    return !!template && template.get('allow_global_scene_designs');
}

export function getTemplateDefaultAspectRatio(state: RootState, templateId: string) {
    const template = getTemplateById(state, templateId);
    if (!template) return null;
    return template.get('default_aspect_ratio') as AspectRatio | null;
}

export const getCustomTemplateCategoryIdForClipCreator = createSelector(
    [(state: RootState) => state.templates.templateCategories, getCategoryIds],
    (templateCategories, categoryIds: Immutable.List<number>) => {
        const orm = new StoreORM<TemplateCategory>(templateCategories);
        const customCategoryId = categoryIds.find((categoryId) => {
            const category = orm.getById(categoryId);
            return category.get('display_name').toLowerCase() === 'custom';
        });
        return customCategoryId;
    },
);

export const getTemplateIdsForClipCreator = createSelector([getTemplateIds], (templateIds) => {
    return templateIds.filter((templateId) => templateId !== CLIP_CREATOR_PREVIEW_TEMPLATE);
});

export function getTemplateSearchQuery(state: RootState): string {
    return state.templates.templateSearchQuery;
}

export function isSceneDesignSelected(state: RootState, sceneDesignId: string): boolean {
    return !!state.templates.sceneDesignSelection.selectedIds[sceneDesignId];
}

export function hasAnySceneDesignSelected(state: RootState): boolean {
    return Object.keys(state.templates.sceneDesignSelection.selectedIds).length > 0;
}

export function getSelectedSceneDesignIds(state: RootState): string[] {
    return Object.keys(state.templates.sceneDesignSelection.selectedIds);
}

/**
 * Gets all template IDs that match the search query, across all categories
 */
export const getAllFilteredTemplateIds = createSelector(
    [
        (state: RootState) =>
            state.templates.templatesList.get('byId') as unknown as Immutable.Map<
                string,
                ImmutableMap<Template>
            >,
        (state: RootState) => getTemplateCategoryIds(state),
        (state: RootState) => state.templates.templateCategories.get('byId'),
        (state: RootState) => getTemplateSearchQuery(state),
    ],
    (allTemplates, categoryIds, allCategories, searchQuery) => {
        if (!searchQuery.trim()) {
            return Immutable.List<string>();
        }

        // Split query by whitespace and filter out empty strings
        const queryWords = searchQuery
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);

        // Collect all template IDs from all categories
        const allTemplateIds = new Set<string>();
        categoryIds.forEach((categoryId) => {
            const category = allCategories.get(categoryId);
            if (category) {
                const templateIds = category.get('templates') || Immutable.List<string>();
                templateIds.forEach((templateId: string) => {
                    allTemplateIds.add(templateId);
                });
            }
        });

        // Filter based on search query
        const filteredIds = Array.from(allTemplateIds).filter((templateId) => {
            const template = allTemplates.get(templateId);
            if (!template) return false;

            const categoryId = template.get('template_category');
            const categoryName = allCategories.get(categoryId).get('display_name') || '';
            const searchableText = [
                template.get('display_name') || '',
                template.get('description') || '',
                templateId,
                categoryName,
            ]
                .join(' ')
                .toLowerCase();

            return queryWords.every((word) => searchableText.includes(word));
        });

        return Immutable.List(filteredIds);
    },
);

export function isGlobalTemplate(templateId: string): boolean {
    return templateId === (GlobalTemplate.GLOBAL_TEMPLATE_ID as string);
}

export function luminaryScenesHaveTemplateReferences(
    luminary: Readonly<Luminary>,
    sceneDesignIds: string[],
): boolean {
    return luminary.scenes
        .filter((scene) => sceneDesignIds.includes(scene.sceneDesign))
        .some((scene) => {
            const referenceMap = scene.buildTemplateReferenceMap();
            return Object.values(referenceMap).some(
                (assetRefs) => Object.keys(assetRefs).length > 0,
            );
        });
}

export const getSceneDesignIdsForSceneCategories = createSelector(
    (state: RootState, templateId: string, sceneCategories: string[]) => sceneCategories,
    (state: RootState, templateId: string) => getTemplateSceneDesigns(state, templateId),
    (state: RootState, templateId: string) => getSupportedAspectRatios(state, templateId),
    (sceneCategories, allSceneDesigns, supportedAspectRatios) =>
        sceneCategories.flatMap((sceneCategory) =>
            allSceneDesigns
                .filter(
                    (sceneDesign) =>
                        getSceneCategoryFromSceneDesign(sceneDesign, supportedAspectRatios) ===
                        sceneCategory,
                )
                .map((sceneDesign) => sceneDesign.id),
        ),
);

export function getSceneDesignIdsForDisplayGroupNames(
    state: RootState,
    templateId: string,
    displayGroupNames: string[],
): string[] {
    const groupedSceneDesignIds = getGroupedSceneDesignIds(state, templateId);
    return displayGroupNames.flatMap((displayGroupName) => {
        const displayGroup = groupedSceneDesignIds.find(
            (group) => group.label.toLowerCase() === displayGroupName.toLowerCase(),
        );
        return displayGroup?.options ?? [];
    });
}
